// Workforce.js  (v2.1)
// v2.1: RAW VIEW IGNORES ITEM COUNTS ENTIRELY — the per-pharmacy item-count
//       line (and its red "no item count recorded" warning) only renders in
//       the normalised view; raw always populates from scheduled hours less
//       unpaid breaks alone. Backend staff filter relaxed to active !=
//       false so tenants that never write the active flag still populate.
//
// Workforce.js  (v2.0)
// -----------------------------------------------------------------------------
// v2.0: LIVE DATA. Grid: pharmacies down the left, roles across the top, fed
// by /api/headoffice/clock/workforce — total contracted hours per role from
// ACTIVE staff only (four weeks of each member's weekly pattern, scheduled
// hours less UNPAID break entitlements), with the latest "item count" figure
// from Periodic Tasks per pharmacy.
//
// Two views (buttons top-right):
//   Raw data (default)        — the contracted hours themselves
//   Normalised per 1000 items — hours / itemCount * 1000
// Colour coding (normalised view only) is anchored on the MEDIAN cell value
// per role column: yellow below (understaffed), green at, red above
// (overstaffed). Raw view is uncoloured — absolute hours aren't comparable
// across branches of different sizes.
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";

const API = "https://api.pharmacegroup.co.uk";
const token = () => localStorage.getItem("token") || localStorage.getItem("authToken") || "";
async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify(body || {})
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
  return d;
}

function median(nums) {
  const arr = [...nums].sort((a, b) => a - b);
  if (arr.length === 0) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}
function mix(a, b, t) {
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * Math.max(0, Math.min(1, t))));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}
function cellColour(value, med, min, max) {
  if (value == null || med == null) return "transparent";
  if (value <= med) {
    const span = med - min;
    const t = span > 0 ? (value - min) / span : 1;
    return mix([253, 224, 71], [134, 239, 172], t); // amber-300 -> green-300
  }
  const span = max - med;
  const t = span > 0 ? (value - med) / span : 0;
  return mix([134, 239, 172], [252, 165, 165], t);   // green-300 -> red-300
}

export default function Workforce() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("raw"); // 'raw' (default) | 'per1000'

  useEffect(() => {
    let dead = false;
    (async () => {
      setLoading(true);
      try {
        const d = await post("/api/headoffice/clock/workforce", {});
        if (!dead) { setData(d); setErr(null); }
      } catch (e) { if (!dead) setErr(e.message); }
      finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, []);

  const roles = data?.roles || [];
  const pharmacies = data?.pharmacies || [];

  // Cell value for the current mode. Raw: hours (null when none). Per-1000:
  // hours / items * 1000 (null when no hours OR no item count).
  const valueOf = (p, role) => {
    const hrs = p.hoursByRole?.[role] || 0;
    if (hrs <= 0) return null;
    if (mode === "raw") return hrs;
    if (!p.itemCount) return null;
    return (hrs / p.itemCount) * 1000;
  };

  const colStats = useMemo(() => {
    const stats = {};
    for (const role of roles) {
      const vals = pharmacies.map(p => valueOf(p, role)).filter(v => v != null);
      stats[role] = { med: median(vals), min: vals.length ? Math.min(...vals) : null, max: vals.length ? Math.max(...vals) : null };
    }
    return stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mode]);

  const th = {
    textAlign: "center", padding: "10px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: "#374151",
    borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap"
  };
  const td = {
    padding: "10px 12px", fontSize: 13, textAlign: "center",
    borderBottom: "1px solid #f0f1f3", borderLeft: "1px solid #f0f1f3"
  };
  const modeBtn = (m, label) => (
    <button onClick={() => setMode(m)}
      style={{ border: "1px solid #0f766e", borderRadius: 6, padding: "5px 12px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
               background: mode === m ? "#0f766e" : "#fff", color: mode === m ? "#fff" : "#0f766e" }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Workforce</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {modeBtn("raw", "Raw data")}
          {modeBtn("per1000", "Normalised per 1000 items")}
        </div>
      </div>
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        {mode === "raw"
          ? "Total contracted hours by role — four weeks of each ACTIVE staff member's weekly pattern, scheduled hours less unpaid break entitlements."
          : `Contracted hours per 1,000 items, by role — normalised on the latest "${data?.itemTaskDescription || "item count"}" figure from Periodic Tasks. Colour is relative to the median pharmacy per role: green on the median, yellow understaffed, red overstaffed.`}
      </div>

      {err && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 10 }}>Error: {err}</div>}
      {loading && <div style={{ color: "#6b7280", fontSize: 13, padding: 12 }}>Loading workforce data…</div>}

      {!loading && !err && (
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#f9fafb" }}>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Pharmacy</th>
                {roles.map(role => (
                  <th key={role} style={th}>
                    {role}
                    <div style={{ fontSize: 9, fontWeight: 400, color: "#6b7280", textTransform: "none", letterSpacing: 0 }}>
                      {mode === "raw" ? "Contracted hours (4 weeks)" : "Hours per 1000 items"}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pharmacies.map(p => (
                <tr key={p.dbName}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>
                    {p.pharmacyName}
                    {mode === "per1000" && (
                      <div style={{ fontSize: 10, fontWeight: 400, color: p.itemCount ? "#6b7280" : "#dc2626" }}>
                        {p.itemCount ? `${p.itemCount.toLocaleString()} items` : "no item count recorded"}
                      </div>
                    )}
                  </td>
                  {roles.map(role => {
                    const v = valueOf(p, role);
                    const s = colStats[role];
                    const bg = mode === "per1000" ? cellColour(v, s.med, s.min, s.max) : "transparent";
                    return (
                      <td key={role} style={{ ...td, background: bg }}>
                        {v == null ? <span style={{ color: "#9ca3af" }}>—</span>
                          : mode === "raw" ? v.toFixed(1) : v.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!pharmacies.length && (
                <tr><td style={{ ...td, textAlign: "left", color: "#6b7280" }}>No pharmacies in scope.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {mode === "per1000" && !loading && !err && (
        <div style={{ display: "flex", gap: 18, marginTop: 12, fontSize: 12, color: "#374151", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 28, height: 12, background: "linear-gradient(90deg, rgb(253,224,71), rgb(134,239,172), rgb(252,165,165))", borderRadius: 2 }} />
            Understaffed → median → overstaffed
          </span>
          <span style={{ color: "#6b7280" }}>(— = no hours for the role, or no item count for the pharmacy)</span>
        </div>
      )}
    </div>
  );
}